import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated, ScrollView, useWindowDimensions } from 'react-native';
import { useDispatch } from 'react-redux';
import { getCatogoriesTypes, getRoomTypes } from '../../../redux/action/maintenance.actions';

import Active_project_openflow_id from '../active-projects/active_project_openflow_id';
import NewIssuePage from '../new-issues/NewIssuePage';
import New_issue_openflow_id from '../new-issues/new_issue_openflow_id';
import ActiveProject from '../active-projects/ActiveProject';
import { COLORS } from '../../tenants/tenantOnboarding/screens/components/PropertyDetailsCard';

const Reports = (props: any) => {
  const [tab, setTab] = useState(props?.initialTab ?? 0);
  const [selectedFlowId, setSelectedFlowId] = useState<number | null>(null);
  const [selectedActiveProjectId, setSelectedActiveProjectId] = useState<number | null>(null);
  const [selectedCompletedProjectId, setSelectedCompletedProjectId] = useState<number | null>(null);
  const [selectedArchivedProjectId, setSelectedArchivedProjectId] = useState<number | null>(null);

  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const tabScrollRef = useRef<ScrollView | null>(null);
  const scrollOffsetRef = useRef(0);
  const tabLayoutsRef = useRef<Record<number, { x: number; width: number }>>({});
  const contentFade = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    dispatch(getRoomTypes());
    dispatch(getCatogoriesTypes());
  }, []);

  useEffect(() => {
    if (typeof props?.initialTab === 'number') {
      setTab(props.initialTab);
    }
  }, [props?.initialTab]);

  useEffect(() => {
    const activeTabLayout = tabLayoutsRef.current[tab];

    if (!activeTabLayout) {
      return;
    }

    const nextOffset = Math.max(0, activeTabLayout.x - width * 0.3);

    tabScrollRef.current?.scrollTo({
      x: nextOffset,
      animated: true,
    });

    scrollOffsetRef.current = nextOffset;
  }, [tab, width]);

  /* ================= TAB CHANGE ================= */

  const handleChange = (index: number) => {
    if (index !== 0) setSelectedFlowId(null);
    if (index !== 1) setSelectedActiveProjectId(null);
    if (index !== 2) setSelectedCompletedProjectId(null);
    if (index !== 3) setSelectedArchivedProjectId(null);

    Animated.sequence([
      Animated.timing(contentFade, {
        toValue: 0,
        duration: 120,
        useNativeDriver: true,
      }),
      Animated.timing(contentFade, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();

    setTab(index);
  };

  const scrollTabsBy = (direction: 'left' | 'right') => {
    const nextOffset = Math.max(0, scrollOffsetRef.current + (direction === 'left' ? -180 : 180));

    tabScrollRef.current?.scrollTo({
      x: nextOffset,
      animated: true,
    });

    scrollOffsetRef.current = nextOffset;
  };

  const handleTabScroll = (event: any) => {
    scrollOffsetRef.current = event?.nativeEvent?.contentOffset?.x ?? 0;
  };

  return (
    <View style={styles.container}>
      <View style={[styles.card, isMobile && styles.cardMobile]}>
        <Text style={[styles.heading, isMobile && styles.headingMobile]}>Issue Reports</Text>

        {/* ================= TABS ================= */}
        <View style={[styles.tabStrip, isMobile && styles.tabStripMobile]}>
          <TouchableOpacity
            style={[styles.scrollArrow, styles.scrollArrowLeft, isMobile && styles.scrollArrowMobile]}
            activeOpacity={0.8}
            onPress={() => scrollTabsBy('left')}
          >
            <Text style={[styles.scrollArrowText, isMobile && styles.scrollArrowTextMobile]}>‹</Text>
          </TouchableOpacity>

          <ScrollView
            ref={tabScrollRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            onScroll={handleTabScroll}
            scrollEventThrottle={16}
            contentContainerStyle={[styles.tabsContainer, isMobile && styles.tabsContainerMobile]}
          >
            {['New Issues', 'Active Projects', 'Completed', 'Archived'].map((label, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.tabButton,
                  isMobile && styles.tabButtonMobile,
                  tab === index && styles.activeTabButton,
                  tab === index && isMobile && styles.activeTabButtonMobile,
                ]}
                activeOpacity={0.85}
                onPress={() => handleChange(index)}
                onLayout={(event) => {
                  tabLayoutsRef.current[index] = {
                    x: event.nativeEvent.layout.x,
                    width: event.nativeEvent.layout.width,
                  };
                }}
              >
                <Text
                  numberOfLines={1}
                  style={[styles.tabText, isMobile && styles.tabTextMobile, tab === index && styles.activeTabText]}
                >
                  {label}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity
            style={[styles.scrollArrow, styles.scrollArrowRight, isMobile && styles.scrollArrowMobile]}
            activeOpacity={0.8}
            onPress={() => scrollTabsBy('right')}
          >
            <Text style={[styles.scrollArrowText, isMobile && styles.scrollArrowTextMobile]}>›</Text>
          </TouchableOpacity>
        </View>

        {/* ================= TAB CONTENT ================= */}
        <Animated.View style={{ opacity: contentFade }}>
          {tab === 0 &&
            (props?.id ? (
              <New_issue_openflow_id
                openflow_id_props={props?.id}
                showCompletePage={false}
                onProjectCreated={() => handleChange(1)}
              />
            ) : selectedFlowId ? (
              <View>
                <TouchableOpacity
                  style={styles.backButton}
                  activeOpacity={0.85}
                  onPress={() => setSelectedFlowId(null)}
                >
                  <Text style={styles.backButtonText}>Back to New Issues</Text>
                </TouchableOpacity>
                <New_issue_openflow_id
                  openflow_id_props={selectedFlowId}
                  showCompletePage={false}
                  onProjectCreated={() => handleChange(1)}
                />
              </View>
            ) : (
              <NewIssuePage showCompletePage={false} onViewFlow={(flow) => setSelectedFlowId(flow?.openflow_id)} />
            ))}

          {tab === 1 &&
            (selectedActiveProjectId ? (
              <View>
                <TouchableOpacity
                  style={styles.backButton}
                  activeOpacity={0.85}
                  onPress={() => setSelectedActiveProjectId(null)}
                >
                  <Text style={styles.backButtonText}>Back to Active Projects</Text>
                </TouchableOpacity>
                <Active_project_openflow_id
                  project_id={selectedActiveProjectId}
                  type_props={'active'}
                  onBack={(datatype) => {
                    setSelectedActiveProjectId(null);
                    if (datatype === 'completed') {
                      handleChange(2);
                    }
                  }}
                />
              </View>
            ) : (
              <ActiveProject
                openflow_id_props={props?.id}
                showCompletePage={false}
                type_props={''}
                onProjectUpdated={() => handleChange(2)}
                onViewProject={(flow) => setSelectedActiveProjectId(flow?.open_flow_maintenance_project_id)}
              />
            ))}

          {tab === 2 &&
            (selectedCompletedProjectId ? (
              <View>
                <TouchableOpacity
                  style={styles.backButton}
                  activeOpacity={0.85}
                  onPress={() => setSelectedCompletedProjectId(null)}
                >
                  <Text style={styles.backButtonText}>Back to Completed Projects</Text>
                </TouchableOpacity>
                <Active_project_openflow_id
                  project_id={selectedCompletedProjectId}
                  type_props={'completed'}
                  onBack={() => {
                    setSelectedCompletedProjectId(null);
                  }}
                />
              </View>
            ) : (
              <ActiveProject
                openflow_id_props={props?.id}
                showCompletePage={false}
                type_props={'completed'}
                onProjectUpdated={() => handleChange(2)}
                onViewProject={(flow) => setSelectedCompletedProjectId(flow?.open_flow_maintenance_project_id)}
              />
            ))}

          {tab === 3 &&
            (selectedArchivedProjectId ? (
              <View>
                <TouchableOpacity
                  style={styles.backButton}
                  activeOpacity={0.85}
                  onPress={() => setSelectedArchivedProjectId(null)}
                >
                  <Text style={styles.backButtonText}>Back to Archived Projects</Text>
                </TouchableOpacity>
                <Active_project_openflow_id
                  project_id={selectedArchivedProjectId}
                  type_props={'archived'}
                  onBack={() => {
                    setSelectedArchivedProjectId(null);
                  }}
                />
              </View>
            ) : (
              <ActiveProject
                openflow_id_props={props?.id}
                showCompletePage={false}
                type_props={'archived'}
                onProjectUpdated={() => handleChange(2)}
                onViewProject={(flow) => setSelectedArchivedProjectId(flow?.open_flow_maintenance_project_id)}
              />
            ))}
        </Animated.View>
      </View>
    </View>
  );
};

export default Reports;

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    backgroundColor: '#F3F4F6',
  },

  card: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 10,
    elevation: 3,
  },
  cardMobile: {
    padding: 8,
  },

  heading: {
    fontSize: 20,
    fontWeight: '700',
    color: '#6D28D9',
    marginBottom: 10,
    marginHorizontal: 20,
    marginTop: 10,
  },
  headingMobile: {
    marginHorizontal: 12,
    marginTop: 6,
    fontSize: 18,
  },

  tabStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    marginHorizontal: 8,
    marginBottom: 6,
  },
  tabStripMobile: {
    marginHorizontal: 2,
  },
  scrollArrow: {
    position: 'absolute',
    top: '50%',
    zIndex: 3,
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.primary,
    transform: [{ translateY: -17 }],
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  scrollArrowLeft: {
    left: 6,
  },
  scrollArrowRight: {
    right: 6,
  },
  scrollArrowMobile: {
    width: 30,
    height: 30,
    transform: [{ translateY: -15 }],
  },
  scrollArrowText: {
    fontSize: 22,
    lineHeight: 22,
    color: '#fff',
    marginTop: -2,
  },
  scrollArrowTextMobile: {
    fontSize: 20,
  },
  tabsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    flexGrow: 1,
    paddingHorizontal: 48,
  },
  tabsContainerMobile: {
    paddingVertical: 4,
    paddingHorizontal: 42,
  },

  tabButton: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginRight: 10,
    borderRadius: 999,
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  tabButtonMobile: {
    paddingVertical: 9,
    paddingHorizontal: 14,
    marginRight: 8,
  },

  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  tabTextMobile: {
    fontSize: 13,
  },

  activeTabButton: {
    backgroundColor: '#EDE9FE',
    borderColor: '#D8B4FE',
  },
  activeTabButtonMobile: {
    backgroundColor: '#EDE9FE',
  },
  activeTabText: {
    color: COLORS.primary,
    fontWeight: '600',
  },

  backButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: '#F2EBFF',
    marginTop: 14,
    marginBottom: 12,
  },
  backButtonText: {
    color: COLORS.primary,
    fontWeight: '700',
    fontSize: 14,
  },
});
